import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useTracking } from '@/hooks/useTracking';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export interface CreateTicketRequest {
  title: string;
  description: string;
  type?: 'bug' | 'feature_request' | 'support' | 'churn_signal' | 'general';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assigneeId?: string;
  tags?: string[];
  dueDate?: string;
  estimatedHours?: number;
  churnSignalId?: string;
  churnSignalData?: {
    signalType: string;
    riskScore: number;
    userEmail: string;
    userName: string;
  };
  columnId?: string;
  position?: number;

  /** Audience member IDs (customers) this ticket concerns. */
  audienceMemberIds?: string[];
}

export interface CreateTicketResponse {
  success: boolean;
  data: {
    ticketId: string;
    title: string;
    description: string;
    status: 'open';
    priority: 'medium';
    type: 'general';
    createdAt: string;
    createdBy: string;
    createdByName: string;
  };
}

async function createTicket(data: CreateTicketRequest, sessionJwt: string | undefined, workspaceId: string) {
  const bearer = resolveBearerToken(sessionJwt);
  const headers = backendRequestHeaders(bearer || undefined);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const response = await axios.post<CreateTicketResponse>(
    `${getBackendBaseUrl()}/workspaces/${workspaceId}/tickets`,
    data,
    { headers },
  );

  return response.data;
}

export function useCreateTicket() {
  const { user } = useAuth();
  const { trackEvent } = useTracking();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const queryClient = useQueryClient();

  // Helper function to extract workspace ID from workspace object
  const extractWorkspaceId = (workspaceObj: any) => {
    if (!workspaceObj) return '';
    // Try id first, then SK, then extract from SK if it starts with "WS#"
    if (workspaceObj.id) return workspaceObj.id;
    if (workspaceObj.SK) {
      // If SK starts with "WS#", extract the UUID part
      if (workspaceObj.SK.startsWith('WS#')) {
        return workspaceObj.SK.substring(3); // Remove "WS#" prefix
      }
      return workspaceObj.SK;
    }
    return '';
  };

  const workspaceId = extractWorkspaceId(workspace);

  return useMutation({
    mutationFn: (data: CreateTicketRequest) => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }

      return createTicket(data, user?.signInUserSession?.idToken?.jwtToken, workspaceId);
    },
    onMutate: async (data: CreateTicketRequest) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries([queryKeys.tickets, workspaceId]);

      // Snapshot the previous value
      const previousTickets = queryClient.getQueryData([queryKeys.tickets, workspaceId]);

      // Optimistically add the ticket to cache
      queryClient.setQueryData([queryKeys.tickets, workspaceId], (old: any) => {
        if (!old) return old;

        console.log('🔮 Optimistic create: Adding new ticket to column', data.columnId);

        // Create temporary ticket with placeholder ID
        const tempTicketId = `temp-${Date.now()}`;
        const tempTicket = {
          ticketId: tempTicketId,
          id: tempTicketId,
          title: data.title,
          description: data.description,
          status: 'open',
          priority: data.priority || 'medium',
          type: data.type || 'general',
          columnId: data.columnId,
          assigneeId: data.assigneeId,
          tags: data.tags || [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          comments: [],
          deleted: false,
          metadata: {},
          position: 0, // Will be updated by backend
        };

        // Add ticket to tickets array
        const newTickets = [...old.tickets, tempTicket];

        // Add ticket ID to column's ticketOrder array
        const newColumns = old.columns.map((col: any) => {
          if (col.id === data.columnId) {
            const newTicketOrder = col.ticketOrder ? [...col.ticketOrder, tempTicketId] : [tempTicketId];
            return { ...col, ticketOrder: newTicketOrder };
          }
          return col;
        });

        console.log(`🔮 Optimistic: Added temporary ticket ${tempTicketId} to UI`);
        return { ...old, tickets: newTickets, columns: newColumns };
      });

      // Return context with the snapshot
      return { previousTickets };
    },
    onSuccess: async (data, variables) => {
      console.log('✅ Ticket created successfully:', data);

      trackEvent('ticket_created', {
        ticket_id: data.data?.ticketId,
        ticket_type: variables.type ?? 'general',
        priority: variables.priority ?? 'medium',
        from_churn_signal: Boolean(variables.churnSignalId),
        churn_signal_id: variables.churnSignalId,
      });

      // Refetch inactive queries too — board may not be mounted (dashboard / member profile).
      await queryClient.invalidateQueries([queryKeys.tickets, workspaceId], { refetchInactive: true });
    },
    onError: (error: any, variables, context: any) => {
      console.error('❌ Ticket creation failed:', error);

      // Rollback to previous state on error
      if (context?.previousTickets) {
        queryClient.setQueryData([queryKeys.tickets, workspaceId], context.previousTickets);
        console.log('🔙 Rolled back to previous state due to error');
      }

      // Handle specific error cases
      if (error.response?.status === 400) {
        throw new Error('Invalid ticket data. Please check your input and try again.');
      } else if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please log in again.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else if (error.response?.status === 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(error.message || 'Failed to create ticket. Please try again.');
      }
    },
  });
}
